import { createFileRoute } from "@tanstack/react-router";

import { Badge } from "@monorepo-apps/ui/src/components/badge";
import { Checkbox } from "@monorepo-apps/ui/src/components/checkbox";
import { Switch } from "@monorepo-apps/ui/src/components/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@monorepo-apps/ui/src/components/table";

import {
  useAllProfiles,
  useUpdateProfileAccess,
} from "@/application/use-cases/profile/use-profile.queries";
import { PageHeader } from "@/presentation/components/shell/page-header";
import { useAuth } from "@/presentation/providers/auth.provider";
import { useWorkspace } from "@/presentation/providers/workspace.provider";
import { WORKSPACES, type WorkspaceId } from "@/presentation/data/workspaces";
import type { ProfileRow } from "@/infrastructure/supabase/database.types";

export const Route = createFileRoute("/_app/users/")({
  component: UsersPage,
});

function UsersPage() {
  const { isAdmin, loading } = useWorkspace();

  return (
    <div className="animate-page-enter mx-auto flex w-full max-w-5xl flex-col">
      <PageHeader
        eyebrow="Admin"
        title="Users"
        summary="Manage which workspaces each user can see, their write access, and who is an admin. Inviting new users is done from the Supabase dashboard."
      />
      <div className="px-6 pt-6 pb-8 lg:px-10">
        {loading ? (
          <p className="text-muted-foreground text-xs italic">Checking access…</p>
        ) : !isAdmin ? (
          <p className="text-muted-foreground text-sm">
            This page is for admins only. Ask an admin to grant you access.
          </p>
        ) : (
          <UsersTable />
        )}
      </div>
    </div>
  );
}

function UsersTable() {
  const { session } = useAuth();
  const usersQ = useAllProfiles();
  const update = useUpdateProfileAccess();
  const selfId = session?.user?.id ?? null;
  const users = usersQ.data ?? [];

  if (usersQ.isLoading) {
    return <p className="text-muted-foreground text-xs italic">Loading users…</p>;
  }
  if (users.length === 0) {
    return <p className="text-muted-foreground text-xs italic">No users found.</p>;
  }

  const setWorkspaces = (user: ProfileRow, id: WorkspaceId, on: boolean) => {
    const next = on
      ? Array.from(new Set([...user.workspaces, id]))
      : user.workspaces.filter((w) => w !== id);
    update.mutate({ id: user.id, patch: { workspaces: next } });
  };

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>User</TableHead>
            {WORKSPACES.map((w) => (
              <TableHead key={w.id} className="text-center">
                {w.label}
              </TableHead>
            ))}
            <TableHead className="text-center">Can write</TableHead>
            <TableHead className="text-center">Admin</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {users.map((user) => {
            const isSelf = user.id === selfId;
            return (
              <TableRow key={user.id}>
                <TableCell className="align-middle">
                  <div className="flex items-center gap-2">
                    <span className="text-[13px] font-medium">{user.email ?? "(no email)"}</span>
                    {isSelf ? (
                      <Badge variant="secondary" className="text-[10px]">
                        you
                      </Badge>
                    ) : null}
                  </div>
                  {user.role ? (
                    <span className="text-muted-foreground text-[11px]">{user.role}</span>
                  ) : null}
                </TableCell>
                {WORKSPACES.map((w) => (
                  <TableCell key={w.id} className="text-center align-middle">
                    <Checkbox
                      checked={user.workspaces.includes(w.id)}
                      onCheckedChange={(c) => setWorkspaces(user, w.id, c === true)}
                      aria-label={`${user.email} can see ${w.label}`}
                    />
                  </TableCell>
                ))}
                <TableCell className="text-center align-middle">
                  <Switch
                    checked={user.can_write}
                    onCheckedChange={(c) => update.mutate({ id: user.id, patch: { can_write: c } })}
                    aria-label={`${user.email} can write`}
                  />
                </TableCell>
                <TableCell className="text-center align-middle">
                  <Switch
                    checked={user.is_admin}
                    disabled={isSelf}
                    onCheckedChange={(c) => update.mutate({ id: user.id, patch: { is_admin: c } })}
                    aria-label={`${user.email} is admin`}
                    title={isSelf ? "You can't change your own admin status" : undefined}
                  />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      {update.isError ? (
        <p className="text-destructive mt-3 text-[11px]">
          A change failed to save — you may not have permission.
        </p>
      ) : null}
    </div>
  );
}
